"use client";

import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import Link from "next/link";

type BillingCycle = "monthly" | "six_months" | "annual";

export function PricingContent() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");

  const plans = [
    {
      name: "Essential Growth",
      monthlyPrice: 99,
      sixMonthPrice: 495,
      annualPrice: 891,
      desc: "Complete profile protection and foundational optimization.",
      features: [
        "24/7 Profile Monitoring & Protection",
        "2 Ready-to-Post Submissions / Edits",
        "Review Monitoring & Response",
        "Business Info & Hours Maintenance",
        "Strategic Keyword Refinement",
        "Monthly Performance Reports",
        "Q&A Management",
        "Direct Phone & Email Support",
        "Assigned Account Manager"
      ],
      portalAccess: {
        title: "Client Portal Access",
        desc: "Submit your ready-to-post content and profile edits through your client portal.",
        allowance: "Up to 2 requests per month"
      },
      cta: "Start Essential Plan",
      popular: false,
      id: "essential"
    },
    {
      name: "Advanced Visibility",
      monthlyPrice: 249,
      sixMonthPrice: 1245,
      annualPrice: 2241,
      desc: "Aggressive market capture and premium content strategy.",
      features: [
        "Everything in Essential Growth",
        "6 Content Requests — We Write, You Approve",
        "2 Proactive SEO Posts",
        "Geo-Targeted Content Strategy",
        "Spam Fighting & Competitor Audits",
        "Review Generation Campaigns",
        "Priority Support Line",
        "Dedicated Account Manager"
      ],
      portalAccess: {
        title: "Client Portal Access",
        desc: "Submit requests and approve drafted content through your client portal.",
        allowance: "Up to 6 requests per month"
      },
      cta: "Start Advanced Plan",
      popular: true,
      id: "elite"
    }
  ];

  return (
    <div className="pt-48 pb-24">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block mb-4 bg-[#34A853]/20 text-[#34A853] border border-[#34A853]/40 text-sm py-1.5 px-4 font-bold tracking-widest uppercase rounded-full">Pricing Plans</span>
          <h1 className="text-5xl md:text-7xl font-display font-black mb-6 tracking-tight">GBP Management <span className="text-[#4285F4] italic">Pricing.</span></h1>

          <div className="flex flex-col items-center gap-6 mt-8">
            <div className="flex p-1 bg-secondary rounded-full shadow-inner">
              {[
                { label: "Monthly", value: "monthly" as BillingCycle },
                { label: "6 Months (1 Month Free)", value: "six_months" as BillingCycle },
                { label: "Annual (3 Months Free)", value: "annual" as BillingCycle }
              ].map((cycle) => (
                <button
                  key={cycle.value}
                  onClick={() => setBillingCycle(cycle.value)}
                  className={`px-6 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                    billingCycle === cycle.value
                      ? "bg-[#4285F4] text-white shadow-lg"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {cycle.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-24">
          {plans.map((plan, i) => {
            let displayPrice = plan.monthlyPrice;
            let subtext = "billed monthly";
            let total = plan.monthlyPrice;

            if (billingCycle === "six_months") {
              displayPrice = Math.round(plan.sixMonthPrice / 6);
              subtext = `billed every 6 months ($${plan.sixMonthPrice})`;
              total = plan.sixMonthPrice;
            } else if (billingCycle === "annual") {
              displayPrice = Math.round(plan.annualPrice / 12);
              subtext = `billed annually ($${plan.annualPrice})`;
              total = plan.annualPrice;
            }

            return (
              <div
                key={i}
                className={`relative overflow-hidden rounded-[3.5rem] border-4 p-10 transition-all hover:shadow-xl ${plan.popular ? 'border-[#4285F4] bg-white shadow-2xl scale-105 z-10' : 'border-muted bg-secondary/10'}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#4285F4] text-white px-8 py-2 rounded-bl-3xl font-bold uppercase tracking-widest text-xs">
                    Most Popular
                  </div>
                )}
                <h3 className="text-3xl font-display font-bold mb-4 uppercase tracking-tighter leading-none">{plan.name}</h3>
                <div className="flex flex-col mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-6xl font-black text-[#4285F4]">${displayPrice}</span>
                    <span className="text-muted-foreground font-bold uppercase tracking-widest text-sm">/mo</span>
                  </div>
                  <span className="text-[#34A853] font-bold text-sm uppercase tracking-widest mt-1">{subtext}</span>
                </div>
                <p className="text-muted-foreground font-medium mb-10 leading-relaxed text-lg">{plan.desc}</p>

                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#34A853] shrink-0" />
                      <span className="font-bold text-sm uppercase tracking-wide">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Client Portal Access Section */}
                <div className="p-5 rounded-2xl bg-[#4285F4]/5 border border-[#4285F4]/20 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-[#4285F4]/10 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-[#4285F4]" />
                    </div>
                    <span className="font-bold text-sm uppercase tracking-wide">{plan.portalAccess.title}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{plan.portalAccess.desc}</p>
                  <p className="text-sm font-bold text-[#4285F4]">{plan.portalAccess.allowance}</p>
                </div>

                <Link
                  href={`/checkout?plan=${plan.id}&cycle=${billingCycle}`}
                  className={`flex items-center justify-center w-full h-16 rounded-full text-xl font-bold shadow-sm transition-all hover:scale-[1.02] ${plan.popular ? 'bg-[#4285F4] hover:bg-[#4285F4]/90 text-white' : 'bg-white border-4 border-[#4285F4] text-[#4285F4] hover:bg-[#4285F4]/5'}`}
                >
                  {plan.cta}
                </Link>
              </div>
            );
          })}
        </div>

        <div className="max-w-3xl mx-auto text-center mb-24">
          <h3 className="text-2xl font-bold mb-4 italic text-muted-foreground">Looking for a custom enterprise solution?</h3>
          <p className="text-lg text-muted-foreground mb-8">We specialize in managing local search for brands across a wide range of industries. Contact our team for custom multi-location pricing.</p>
          <Link href="/contact" className="text-[#4285F4] font-black text-lg uppercase tracking-widest hover:underline">
            Contact Enterprise Sales →
          </Link>
        </div>

        {/* Upgrade to Full SEO Section */}
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-[3rem] bg-gradient-to-br from-secondary to-secondary/80 border-2 border-muted p-12 md:p-16">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#4285F4]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#34A853]/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            <div className="relative z-10">
              <span className="inline-block mb-4 bg-[#4285F4]/10 text-[#4285F4] border border-[#4285F4]/20 text-xs py-1.5 px-4 font-bold tracking-widest uppercase rounded-full">
                Ready for the Next Level?
              </span>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 tracking-tight">
                Outgrow GBP?<br />
                <span className="text-muted-foreground">Upgrade to Full SEO.</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed">
                GBP management is just the beginning. When you're ready to dominate organic search results, drive more website traffic, and capture leads beyond Google Maps, our parent company offers comprehensive website SEO services.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#4285F4]/10 rounded-full flex items-center justify-center">
                    <span className="text-lg">🎯</span>
                  </div>
                  <span className="font-bold">Full Website SEO</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#34A853]/10 rounded-full flex items-center justify-center">
                    <span className="text-lg">📈</span>
                  </div>
                  <span className="font-bold">Content Marketing</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FBBC04]/10 rounded-full flex items-center justify-center">
                    <span className="text-lg">🔗</span>
                  </div>
                  <span className="font-bold">Link Building</span>
                </div>
              </div>
              <a 
                href="https://www.silveroakdigitalmarketing.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-[#4285F4] text-white hover:bg-[#4285F4]/90 rounded-full h-14 px-10 font-bold text-lg transition-colors"
                data-testid="button-upgrade-seo"
              >
                Explore Full SEO Services →
              </a>
              <p className="mt-6 text-sm text-muted-foreground">
                Powered by Silver Oak Digital Marketing
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
